import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Auth via httpOnly cookie (`session_token`) — token is NOT stored in JS/localStorage.
 * Backend sets Secure, httpOnly, SameSite=None cookie on /auth/profile.
 * All axios calls run with `withCredentials: true` so the cookie flows automatically.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // authHeaders is a no-op now (kept for API compatibility with existing callers)
  const authHeaders = useCallback(() => ({}), []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check if returning from OAuth callback — callback will exchange session_id
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const exchangeSessionId = async (sessionId) => {
    const r = await api.post("/auth/profile", null, { headers: { "X-Session-ID": sessionId } });
    // Backend sets httpOnly cookie in the response — we only keep user info in memory
    setUser(r.data.user);
    return r.data.user;
  };

  // Email/password auth (Iter 19)
  const emailLogin = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  };

  const emailRegister = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data.user);
    return r.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, exchangeSessionId, emailLogin, emailRegister, logout, authHeaders, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
